/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React from 'react';

/**
 * Minimal markdown renderer for help cards and assistant answers.
 *
 * It builds React elements directly — never `dangerouslySetInnerHTML` — so help text coming from the
 * database (admin-edited, and machine-translated) cannot inject markup into the portal.
 *
 * Supported, because that is all the help cards use: `##`/`###` headings, paragraphs, ordered and
 * unordered lists with one level of nesting, `**bold**`, `` `code` ``, `[text](url)`, and `---`.
 * Anything else renders as plain text rather than being dropped.
 */

type Inline = React.ReactNode;

/** Split one line into bold / code / link / text runs. */
function renderInline(text: string, keyBase: string): Inline[] {
  const out: Inline[] = [];
  // One pass over the three inline forms, longest-delimiter-first so `**` wins over `*`.
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyBase}-i${i++}`;

    if (token.startsWith('**')) {
      out.push(<strong key={key} className="font-semibold text-gray-900">{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      out.push(
        <code key={key} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em] text-gray-800">
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (linkMatch) {
        const href = linkMatch[2];
        const internal = href.startsWith('/');
        out.push(
          <a
            key={key}
            href={href}
            target={internal ? undefined : '_blank'}
            rel={internal ? undefined : 'noopener noreferrer'}
            className="text-primary-700 underline hover:text-primary-800"
          >
            {linkMatch[1]}
          </a>,
        );
      } else {
        out.push(token);
      }
    }
    last = match.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

interface Block {
  type: 'p' | 'h2' | 'h3' | 'ol' | 'ul' | 'hr';
  lines: { text: string; nested: boolean }[];
}

function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');

  const push = (type: Block['type'], text: string, nested = false) => {
    const prev = blocks[blocks.length - 1];
    if (prev && prev.type === type && (type === 'ol' || type === 'ul' || type === 'p')) {
      prev.lines.push({ text, nested });
    } else {
      blocks.push({ type, lines: [{ text, nested }] });
    }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.trim().length === 0) {
      // A blank line ends the current run, so the next paragraph/list starts fresh.
      blocks.push({ type: 'hr', lines: [] });
      blocks[blocks.length - 1].type = 'p';
      blocks[blocks.length - 1].lines = [];
      continue;
    }
    if (/^---+$/.test(line.trim())) { blocks.push({ type: 'hr', lines: [] }); continue; }

    const indent = line.length - line.trimStart().length;
    const t = line.trim();

    if (/^###\s+/.test(t)) { blocks.push({ type: 'h3', lines: [{ text: t.replace(/^###\s+/, ''), nested: false }] }); continue; }
    if (/^##\s+/.test(t)) { blocks.push({ type: 'h2', lines: [{ text: t.replace(/^##\s+/, ''), nested: false }] }); continue; }
    if (/^\d+\.\s+/.test(t)) { push('ol', t.replace(/^\d+\.\s+/, ''), indent >= 3); continue; }
    if (/^[-*]\s+/.test(t)) { push('ul', t.replace(/^[-*]\s+/, ''), indent >= 2); continue; }

    // Lazy continuation: a plain line straight after a list item is the rest of that item, not a new
    // paragraph. Help cards wrap long steps across lines, and splitting them read as broken sentences.
    const open = blocks[blocks.length - 1];
    if (open && (open.type === 'ol' || open.type === 'ul') && open.lines.length > 0) {
      const lastItem = open.lines[open.lines.length - 1];
      lastItem.text = `${lastItem.text} ${t}`;
      continue;
    }

    push('p', t);
  }

  // Drop the empty paragraph markers used as separators.
  return blocks.filter((b) => b.type === 'hr' || b.lines.length > 0);
}

export const MiniMarkdown: React.FC<{ markdown: string; className?: string }> = ({
  markdown,
  className,
}) => {
  const blocks = parseBlocks(markdown);

  return (
    <div className={className}>
      {blocks.map((block, bi) => {
        const key = `b${bi}`;
        if (block.type === 'hr') return <hr key={key} className="my-3 border-gray-200" />;
        if (block.type === 'h2') {
          return (
            <h3 key={key} className="mb-1 mt-4 text-sm font-semibold text-gray-900">
              {renderInline(block.lines[0].text, key)}
            </h3>
          );
        }
        if (block.type === 'h3') {
          return (
            <h4 key={key} className="mb-1 mt-3 text-[13px] font-semibold text-gray-800">
              {renderInline(block.lines[0].text, key)}
            </h4>
          );
        }
        if (block.type === 'ol' || block.type === 'ul') {
          const Tag = block.type === 'ol' ? 'ol' : 'ul';
          // The indent is inline-START, not left: in Persian and Urdu the markers must sit on the right.
          // Written as an inline style rather than Tailwind's ps-5/ms-4 because Bootstrap's utilities are
          // also loaded here and its `.ps-5 { padding-left: 3rem !important }` wins over the logical class.
          return (
            <Tag
              key={key}
              style={{ paddingInlineStart: '1.25rem', paddingInlineEnd: 0 }}
              className={`mb-2 space-y-1 text-[13px] leading-relaxed text-gray-700 ${
                block.type === 'ol' ? 'list-decimal' : 'list-disc'
              }`}
            >
              {block.lines.map((l, li) => (
                <li
                  key={`${key}-${li}`}
                  style={l.nested ? { marginInlineStart: '1rem' } : undefined}
                  className={l.nested ? 'list-[circle]' : undefined}
                >
                  {renderInline(l.text, `${key}-${li}`)}
                </li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={key} className="mb-2 text-[13px] leading-relaxed text-gray-700">
            {block.lines.map((l, li) => (
              <React.Fragment key={`${key}-${li}`}>
                {li > 0 && ' '}
                {renderInline(l.text, `${key}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

export default MiniMarkdown;
